// Envelope encryption per denúncia (D-22, CRY-01, CRY-04). AAD=caseId prevents cross-case swap (D-23).
// Each denúncia has its own DEK (AES-256-GCM, 32 bytes random).
// Ciphertext is AEAD — tamper-evident + bound to caseId.

// TS 6.x: crypto.subtle requires ArrayBuffer, not ArrayBufferLike.
// Helper to get a proper ArrayBuffer from Uint8Array.
function toBuffer(arr: Uint8Array): ArrayBuffer {
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer;
}

/**
 * Generates a fresh AES-256-GCM DEK as 32 raw bytes.
 * Each denúncia MUST have a unique DEK (CRY-01).
 */
export function generateDek(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Divergência 3 — AAD dos canais de mensagem comitê↔denunciante.
 *
 * Amarra o ciphertext a `caseId` + `ulid` DA MENSAGEM: impede mover a mensagem
 * entre casos (anti-swap, como AAD=caseId) E impede reordenar/replayar uma mensagem
 * dentro do mesmo caso (o ulid é único por mensagem). Domain-separated de AAD=caseId
 * (usada no corpo/pareceres/atas/notificações legadas) pelo prefixo `msg:v1:`.
 *
 * ⚠️ FONTE ÚNICA — os DOIS lados compõem a AAD por esta função: web-comite (envia,
 * Div3 Fatia B) e web-intake (lê/responde, Fatia C). Uma divergência de 1 byte faz
 * a decifra falhar (tag AES-GCM). Aplicável SÓ às origens Div3 (`comite_message` /
 * `denunciante_message`); notificações legadas seguem AAD=`caseId`.
 */
export function messageAad(caseId: string, ulid: string): string {
  return `msg:v1:${caseId}:${ulid}`;
}

/**
 * Encrypts UTF-8 text with the DEK; AAD binds ciphertext to caseId
 * to prevent cross-case swap attacks (CRY-04).
 */
export async function encryptPayload(
  plaintextUtf8: string,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const dek = await crypto.subtle.importKey('raw', toBuffer(dekRaw), { name: 'AES-GCM' }, false, [
    'encrypt',
  ]);
  const aad = new TextEncoder().encode(caseId);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBuffer(nonce), additionalData: toBuffer(aad) },
    dek,
    new TextEncoder().encode(plaintextUtf8),
  );
  return { ciphertext: new Uint8Array(ct), nonce };
}

/**
 * Decrypts; throws on AAD mismatch or tag failure.
 * Returns the original UTF-8 string.
 */
export async function decryptPayload(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<string> {
  const dek = await crypto.subtle.importKey('raw', toBuffer(dekRaw), { name: 'AES-GCM' }, false, [
    'decrypt',
  ]);
  const aad = new TextEncoder().encode(caseId);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBuffer(nonce), additionalData: toBuffer(aad) },
    dek,
    toBuffer(ciphertext),
  );
  return new TextDecoder().decode(pt);
}

/**
 * Encrypts arbitrary bytes (e.g. binary attachments) with AAD=caseId (CRY-04).
 * Same construction as `encryptPayload` but skips UTF-8 encoding so non-text
 * payloads (PNG/PDF/etc) round-trip cleanly.
 */
export async function encryptBytes(
  plaintext: Uint8Array,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const dek = await crypto.subtle.importKey('raw', toBuffer(dekRaw), { name: 'AES-GCM' }, false, [
    'encrypt',
  ]);
  const aad = new TextEncoder().encode(caseId);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBuffer(nonce), additionalData: toBuffer(aad) },
    dek,
    toBuffer(plaintext),
  );
  return { ciphertext: new Uint8Array(ct), nonce };
}

/** Binary counterpart to decryptPayload. Throws on AAD mismatch or tag failure. */
export async function decryptBytes(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<Uint8Array> {
  const dek = await crypto.subtle.importKey('raw', toBuffer(dekRaw), { name: 'AES-GCM' }, false, [
    'decrypt',
  ]);
  const aad = new TextEncoder().encode(caseId);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBuffer(nonce), additionalData: toBuffer(aad) },
    dek,
    toBuffer(ciphertext),
  );
  return new Uint8Array(pt);
}
